import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PaletteMode } from '@mui/material';

const COLOR_MODE_STORAGE_KEY = 'camaras-color-mode';

function readStoredColorMode(): PaletteMode {
  if (typeof window === 'undefined') return 'light';
  try {
    const v = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    /* ignore */
  }
  return 'light';
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

interface UIState {
  snackbar: SnackbarState;
  sidebarOpen: boolean;
  colorMode: PaletteMode;
}

const initialState: UIState = {
  snackbar: { open: false, message: '', severity: 'info' },
  sidebarOpen: true,
  colorMode: readStoredColorMode(),
};

const slice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    showMessage(state, action: PayloadAction<{ message: string; severity?: SnackbarState['severity'] }>) {
      state.snackbar = {
        open: true,
        message: action.payload.message,
        severity: action.payload.severity ?? 'info',
      };
    },
    hideMessage(state) {
      state.snackbar.open = false;
    },
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebar(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload;
    },
    setColorMode(state, action: PayloadAction<PaletteMode>) {
      state.colorMode = action.payload;
      try {
        localStorage.setItem(COLOR_MODE_STORAGE_KEY, action.payload);
      } catch {
        /* ignore */
      }
    },
    toggleColorMode(state) {
      const next: PaletteMode = state.colorMode === 'dark' ? 'light' : 'dark';
      state.colorMode = next;
      try {
        localStorage.setItem(COLOR_MODE_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
    },
  },
});

export const { showMessage, hideMessage, toggleSidebar, setSidebar, setColorMode, toggleColorMode } = slice.actions;
export default slice.reducer;
